import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import ReactMarkdown from "react-markdown";
import {
  Sparkle, ChartLineUp, Megaphone, Target, Handshake,
  ArrowRight, X, CircleNotch, ArrowsClockwise,
  Lightning, CheckCircle, WarningCircle, Info, Star
} from "@phosphor-icons/react";

const INSIGHT_TYPES = [
  {
    key: "performance",
    title: "Performance Analysis",
    description: "Detailed breakdown of your networking ROI, attendance impact, and actionable growth tips.",
    icon: ChartLineUp,
    gradient: "from-teal-400 to-emerald-500",
    bg: "bg-emerald-50 stat-card-emerald",
  },
  {
    key: "pitch",
    title: "Elevator Pitch Generator",
    description: "A compelling 60-second pitch tailored for your business to use in chapter meetings.",
    icon: Megaphone,
    gradient: "from-amber-400 to-orange-500",
    bg: "bg-amber-50 stat-card-amber",
  },
  {
    key: "lead_score",
    title: "Lead Scoring",
    description: "Score your open referral leads from 1-10 based on conversion potential with reasoning.",
    icon: Target,
    gradient: "from-rose-400 to-pink-500",
    bg: "bg-rose-50 stat-card-rose",
  },
  {
    key: "referral_match",
    title: "Referral Match Finder",
    description: "Discover the best referral partners in your chapter based on business synergy analysis.",
    icon: Handshake,
    gradient: "from-violet-400 to-purple-500",
    bg: "bg-violet-50 stat-card-violet",
  },
];

/* ---- Rich Markdown Renderer ---- */
const InsightRenderer = ({ content }) => {
  const mdComponents = {
    h1: ({ children }) => (
      <div className="mb-5 mt-2">
        <div className="flex items-center gap-3">
          <div className="w-1.5 h-8 rounded-full bg-gradient-to-b from-bch-teal to-emerald-400" />
          <h1 className="text-xl font-display font-bold text-primary">{children}</h1>
        </div>
      </div>
    ),
    h2: ({ children }) => (
      <div className="mb-4 mt-6 first:mt-0">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full bg-gradient-to-b from-amber-400 to-orange-400" />
          <h2 className="text-lg font-display font-semibold text-primary">{children}</h2>
        </div>
      </div>
    ),
    h3: ({ children }) => (
      <div className="mb-3 mt-5 first:mt-0">
        <h3 className="text-base font-semibold text-primary flex items-center gap-2">
          <Lightning className="w-4 h-4 text-amber-500 shrink-0" weight="fill" />
          {children}
        </h3>
      </div>
    ),
    h4: ({ children }) => (
      <h4 className="text-sm font-semibold text-primary mb-2 mt-4">{children}</h4>
    ),
    p: ({ children }) => (
      <p className="text-secondary text-sm leading-relaxed mb-3">{children}</p>
    ),
    strong: ({ children }) => (
      <strong className="text-primary font-semibold">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="text-bch-teal font-medium not-italic">{children}</em>
    ),
    ul: ({ children }) => (
      <ul className="space-y-2 mb-4 ml-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="space-y-3 mb-4 ml-1 counter-reset-custom">{children}</ol>
    ),
    li: ({ children, ordered, index }) => (
      <li className="flex items-start gap-2.5 text-sm text-secondary leading-relaxed">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-bch-teal shrink-0" />
        <span className="flex-1">{children}</span>
      </li>
    ),
    hr: () => (
      <div className="my-5 flex items-center gap-3">
        <div className="flex-1 h-px bg-[var(--border-color)]" />
        <Sparkle className="w-3.5 h-3.5 text-muted/30" weight="fill" />
        <div className="flex-1 h-px bg-[var(--border-color)]" />
      </div>
    ),
    blockquote: ({ children }) => (
      <div className="my-4 p-4 bg-bch-teal/5 border-l-3 border-bch-teal rounded-r-xl">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-bch-teal shrink-0 mt-0.5" weight="fill" />
          <div className="text-sm text-secondary [&>p]:mb-0">{children}</div>
        </div>
      </div>
    ),
    code: ({ children, inline }) => {
      if (inline) {
        return (
          <code className="px-1.5 py-0.5 bg-bch-teal/10 text-bch-teal text-xs font-mono rounded-md">{children}</code>
        );
      }
      return (
        <pre className="my-4 p-4 bg-surface border border-[var(--border-color)] rounded-xl overflow-x-auto">
          <code className="text-xs font-mono text-secondary">{children}</code>
        </pre>
      );
    },
    a: ({ href, children }) => (
      <a href={href} className="text-bch-teal underline underline-offset-2 hover:text-bch-teal/80 transition-colors" target="_blank" rel="noreferrer">{children}</a>
    ),
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto border border-[var(--border-color)] rounded-xl">
        <table className="w-full text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-surface text-primary font-semibold">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="px-4 py-2.5 text-left text-xs uppercase tracking-wider border-b border-[var(--border-color)]">{children}</th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2.5 text-secondary border-b border-[var(--border-color)]">{children}</td>
    ),
  };

  return (
    <div className="ai-insight-content">
      <ReactMarkdown components={mdComponents}>{content}</ReactMarkdown>
    </div>
  );
};

export default function AIInsightsPage() {
  const { token } = useAuth();
  const [activeInsight, setActiveInsight] = useState(null);
  const [insightData, setInsightData] = useState({});
  const [loading, setLoading] = useState(null);

  const fetchInsight = async (type) => {
    if (insightData[type]) {
      setActiveInsight(type);
      return;
    }
    setLoading(type);
    setActiveInsight(type);
    try {
      const res = await axios.post(`${API}/ai/insights`, {
        insight_type: type
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInsightData(prev => ({ ...prev, [type]: res.data.insight }));
    } catch (err) {
      toast.error("Failed to generate insight. Please try again.");
      setActiveInsight(null);
    } finally {
      setLoading(null);
    }
  };

  const regenerate = async (type) => {
    setLoading(type);
    try {
      const res = await axios.post(`${API}/ai/insights`, {
        insight_type: type
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInsightData(prev => ({ ...prev, [type]: res.data.insight }));
    } catch (err) {
      toast.error("Failed to regenerate. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const activeCfg = INSIGHT_TYPES.find(t => t.key === activeInsight);

  return (
    <>
      <SEO title="AI Insights" description="Get AI-powered business insights — performance analysis, pitch suggestions, and referral matching." path="/ai-insights" noIndex />
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-display text-primary flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
              <Sparkle className="w-5 h-5 text-white" weight="fill" />
            </div>
            AI Insights
          </h1>
          <p className="text-secondary mt-2 text-sm md:text-base">
            AI-powered analysis and recommendations to maximize your networking ROI.
          </p>
        </div>

        {/* Insight Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="insight-cards-grid">
          {INSIGHT_TYPES.map((item) => {
            const Icon = item.icon;
            const isActive = activeInsight === item.key;
            const isLoading = loading === item.key;
            const hasData = !!insightData[item.key];

            return (
              <button
                key={item.key}
                onClick={() => fetchInsight(item.key)}
                disabled={isLoading}
                data-testid={`insight-card-${item.key}`}
                className={`${item.bg} border rounded-2xl p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group ${
                  isActive
                    ? "border-bch-teal shadow-lg ring-2 ring-bch-teal/20"
                    : "border-[var(--border-color)]"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center shrink-0 shadow-md group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-primary font-semibold text-sm">{item.title}</h3>
                      {hasData && !isLoading && (
                        <span className="text-[10px] bg-bch-teal/15 text-bch-teal px-1.5 py-0.5 rounded-md font-medium">Ready</span>
                      )}
                    </div>
                    <p className="text-muted text-xs mt-1 line-clamp-2">{item.description}</p>
                  </div>
                  {isLoading ? (
                    <CircleNotch className="w-5 h-5 text-bch-teal animate-spin shrink-0" weight="bold" />
                  ) : (
                    <ArrowRight className="w-4 h-4 text-muted group-hover:text-bch-teal shrink-0 transition-colors" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Active Insight Result */}
        {activeInsight && (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl shadow-sm overflow-hidden" data-testid="insight-result">
            {/* Result Header */}
            <div className="p-5 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {activeCfg && (
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${activeCfg.gradient} flex items-center justify-center shadow-md`}>
                      {(() => { const Icon = activeCfg.icon; return <Icon className="w-5 h-5 text-white" weight="fill" />; })()}
                    </div>
                  )}
                  <div>
                    <h2 className="text-primary font-semibold text-base">{activeCfg?.title}</h2>
                    {loading === activeInsight && (
                      <p className="text-bch-teal text-xs mt-0.5 flex items-center gap-1">
                        <CircleNotch className="w-3 h-3 animate-spin" weight="bold" />
                        Analyzing your data...
                      </p>
                    )}
                    {insightData[activeInsight] && loading !== activeInsight && (
                      <p className="text-muted text-xs mt-0.5 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-emerald-500" weight="fill" />
                        Analysis complete
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {insightData[activeInsight] && (
                    <button
                      onClick={() => regenerate(activeInsight)}
                      disabled={loading === activeInsight}
                      data-testid="regenerate-btn"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-bch-teal border border-bch-teal/30 rounded-lg hover:bg-bch-teal/5 disabled:opacity-50 font-medium transition-all"
                    >
                      <ArrowsClockwise className={`w-3.5 h-3.5 ${loading === activeInsight ? "animate-spin" : ""}`} weight="bold" />
                      Regenerate
                    </button>
                  )}
                  <button
                    onClick={() => setActiveInsight(null)}
                    className="p-2 hover:bg-surface rounded-xl transition-colors"
                    data-testid="close-insight-btn"
                  >
                    <X className="w-4 h-4 text-muted" />
                  </button>
                </div>
              </div>
            </div>

            {/* Result Content */}
            <div className="p-6 md:p-8">
              {loading === activeInsight && !insightData[activeInsight] ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-bch-teal to-emerald-500 flex items-center justify-center shadow-xl">
                      <Sparkle className="w-8 h-8 text-white animate-pulse" weight="fill" />
                    </div>
                    <div className="absolute -inset-2 rounded-3xl bg-bch-teal/10 animate-ping" style={{ animationDuration: "2s" }} />
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-primary font-medium text-sm">AI is analyzing your data</p>
                    <p className="text-muted text-xs mt-1">This may take 10-15 seconds...</p>
                  </div>
                </div>
              ) : insightData[activeInsight] ? (
                <InsightRenderer content={insightData[activeInsight]} />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
